/**
 * TrainingHeatmap - GitHub-style contribution heatmap for training days
 */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Flame } from 'lucide-react';
import {
  format,
  parseISO,
  eachDayOfInterval,
  subDays,
  startOfWeek,
  getDay,
  isSameDay
} from 'date-fns';
import { tr } from 'date-fns/locale';

const TrainingHeatmap = ({ workouts, dateRange }) => {
  // Build heatmap data
  const heatmapData = useMemo(() => {
    const today = new Date();
    const startDate = subDays(today, dateRange);

    // Adjust to start from a Sunday
    const adjustedStart = startOfWeek(startDate, { weekStartsOn: 0 });

    // Get all days in range
    const days = eachDayOfInterval({ start: adjustedStart, end: today });

    // Count workouts per day
    const workoutCounts = {};
    workouts.forEach(w => {
      const date = w.date;
      workoutCounts[date] = (workoutCounts[date] || 0) + 1;
    });

    // Find max for intensity calculation
    const maxCount = Math.max(...Object.values(workoutCounts), 1);

    // Map days to heatmap cells
    return days.map(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const count = workoutCounts[dateStr] || 0;
      const intensity = count > 0 ? Math.ceil((count / maxCount) * 4) : 0;

      return {
        date: day,
        dateStr,
        count,
        intensity, // 0-4 scale
        dayOfWeek: getDay(day),
      };
    });
  }, [workouts, dateRange]);

  // Group by weeks
  const weeks = useMemo(() => {
    const result = [];
    let currentWeek = [];

    heatmapData.forEach((day, idx) => {
      currentWeek.push(day);
      if (day.dayOfWeek === 6 || idx === heatmapData.length - 1) {
        result.push(currentWeek);
        currentWeek = [];
      }
    });

    return result;
  }, [heatmapData]);

  // Get month labels
  const monthLabels = useMemo(() => {
    const labels = [];
    let lastMonth = -1;

    weeks.forEach((week, weekIdx) => {
      const firstDay = week[0];
      const month = firstDay.date.getMonth();

      if (month !== lastMonth) {
        labels.push({
          weekIdx,
          label: format(firstDay.date, 'MMM', { locale: tr }),
        });
        lastMonth = month;
      }
    });

    return labels;
  }, [weeks]);

  // Intensity colors
  const getIntensityColor = (intensity) => {
    const colors = [
      'bg-gray-800', // 0 - no activity
      'bg-orange-900/50', // 1 - low
      'bg-orange-700/60', // 2 - medium-low
      'bg-orange-500/70', // 3 - medium-high
      'bg-orange-400', // 4 - high
    ];
    return colors[intensity] || colors[0];
  };

  const dayLabels = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="bg-gray-900 border border-gray-800 rounded-xl p-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold text-white">Antrenman Takvimi</h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span>Az</span>
          <div className="flex gap-1">
            {[0, 1, 2, 3, 4].map(i => (
              <div
                key={i}
                className={`w-3 h-3 rounded-sm ${getIntensityColor(i)}`}
              />
            ))}
          </div>
          <span>Çok</span>
        </div>
      </div>

      {/* Heatmap */}
      <div className="overflow-x-auto">
        <div className="inline-block">
          {/* Month labels */}
          <div className="flex ml-8 mb-1">
            {monthLabels.map((label, idx) => (
              <div
                key={idx}
                className="text-xs text-gray-500"
                style={{
                  marginLeft: idx === 0 ? 0 : `${(label.weekIdx - (monthLabels[idx - 1]?.weekIdx || 0)) * 14 - 20}px`,
                }}
              >
                {label.label}
              </div>
            ))}
          </div>

          <div className="flex">
            {/* Day labels */}
            <div className="flex flex-col justify-around mr-2 text-xs text-gray-500">
              {dayLabels.filter((_, i) => i % 2 === 1).map(label => (
                <span key={label} className="h-[14px] leading-[14px]">{label}</span>
              ))}
            </div>

            {/* Weeks grid */}
            <div className="flex gap-[2px]">
              {weeks.map((week, weekIdx) => (
                <div key={weekIdx} className="flex flex-col gap-[2px]">
                  {/* Fill empty days at start of first week */}
                  {weekIdx === 0 && week[0]?.dayOfWeek > 0 && (
                    Array(week[0].dayOfWeek).fill(null).map((_, i) => (
                      <div key={`empty-${i}`} className="w-3 h-3" />
                    ))
                  )}
                  {week.map(day => (
                    <div
                      key={day.dateStr}
                      className={`
                        w-3 h-3 rounded-sm cursor-pointer transition-all
                        ${getIntensityColor(day.intensity)}
                        hover:ring-1 hover:ring-white/50
                      `}
                      title={`${format(day.date, 'd MMMM yyyy', { locale: tr })}: ${day.count} set`}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-800">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-400" />
          <span className="text-sm text-gray-400">
            <span className="text-white font-medium">
              {heatmapData.filter(d => d.count > 0).length}
            </span>
            {' '}antrenman günü
          </span>
        </div>
        <span className="text-gray-600">•</span>
        <span className="text-sm text-gray-400">
          <span className="text-white font-medium">
            {workouts.length}
          </span>
          {' '}toplam set
        </span>
      </div>
    </motion.div>
  );
};

export default TrainingHeatmap;
